import { useState, useEffect, HTMLAttributes, memo } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Game } from '../../../../functions/src/types/firestore/Game';
import { CollectionReference } from 'firebase/firestore';

export type GameAutocompleteProps = {
  setGame: (gameId: string) => void;
};

// TODO: abstract to own component; reused elsewhere
// eslint-disable-next-line @blumintinc/blumint/require-memo
function CustomBox({ children, ...rest }: HTMLAttributes<HTMLElement>) {
  return (
    <>
      <Box
        component="ul"
        sx={{
          zIndex: 1,
          py: 2,
        }}
        {...rest}
      >
        {children}
      </Box>
    </>
  );
}

export const GameAutocomplete = memo(function GameAutocompleteUnmemoized({
  setGame,
}: GameAutocompleteProps) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [options, setOptions] = useState<
    readonly { title: string; id: string }[]
  >([]);

  useEffect(() => {
    const handler = async () => {
      const { firestore } = await import(
        '../../../config/firebase-client/firestore'
      );
      const { getDocs, collection } = await import('firebase/firestore');
      // TODO: games will have to be filtered based on whether or not you can
      // make an event type for them. If you cannot make any, they should not
      // appear here. Otherwise, they should have the options enabled/disabled
      // as appropriate in the next section
      const gameDocs = await getDocs(
        collection(firestore, 'Game') as CollectionReference<Game<Date>>,
      );
      setOptions(
        gameDocs.docs.map((doc) => {
          const game = doc.data();
          return { title: game.title, id: game.id };
        }),
      );
    };
    handler();
  }, []);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      getOptionLabel={({ title }) => {
        return title;
      }}
      disableClearable
      noOptionsText={'Loading games...'}
      id="games-autocomplete"
      options={options}
      isOptionEqualToValue={(option, optionValue) => {
        return option.id === optionValue.id;
      }}
      componentsProps={{
        paper: {
          sx: {
            background: theme.palette.background.elevation[8],
            '&::before': {
              content: '""',
              position: 'absolute',
              height: '100%',
              width: '100%',
              backdropFilter: theme.glass[8],
              zIndex: -1,
              top: 0,
              left: 0,
            },
            boxShadow: theme.shadows[2],
          },
        },
      }}
      ListboxComponent={(props) => {
        return <CustomBox {...props} />;
      }}
      ListboxProps={{
        style: {
          padding: 0,
        },
        onClick: async (e) => {
          const { target } = e;
          if (!target['id']) {
            return;
          }
          const gameId = target['id'];
          setGame(gameId);
          setError(undefined);
        },
      }}
      sx={{
        width: '100%',
        alignItems: 'center',
      }}
      renderOption={(props, option) => {
        // const isFirst = props[`data-option-index`] === 0;
        return (
          <Box
            sx={{
              boxShadow: 'inset 0px -1px 0px rgba(0, 100, 233, 0.12)',
              fontStyle: 'normal',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '200%',
              zIndex: 1,
              letterSpacing: '0.15px',
              textTransform: 'uppercase',
              ':hover': {
                background: '#00294d !important',
              },
            }}
            component="li"
            {...{ ...props, ...option }}
          >
            {option.title}
          </Box>
        );
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            size="small"
            error={!!error}
            helperText={error}
            InputProps={{
              ...params.InputProps,
              type: 'text',
              endAdornment: params.InputProps.endAdornment,
            }}
            label="Search games"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              '&:hover': {
                label: {
                  display: 'unset',
                },
              },
            }}
          />
        );
      }}
    />
  );
});
